export type AllRoutes =
  | "/home"
  | "/clients"
  | "/assessment-reports"
  | "/assessment-reports-legacy"
  | "/assessment-reports?trigger-walkthrough=true"
  | "/qa-criteria"
  | "/goal-bank"
  | "/downloads"
  | "/session-analyzer"
  | "/evaluations"
  | "/video-sessions"
  | "/video-sessions-v2"
  | "/analytics"
  | "/admin-analytics"
  | "/clinical-reports"
  | "/templates"
  | "/frontera-clarity"
  | "/frontera-clarity/expert-chat"
  | "/frontera-clarity/billing"
  | "/frontera-clarity/scheduling"
  | "/frontera-clarity/my-availability"
  | "/notifications/organization"
  | "/notifications/user"
  | "/integrations"
  | "/settings/profile"
  | "/settings/signature"
  | "/leads"
  | "/clinics"
  | "/onboarding"
  | "/release-notes"
  | "/members";

export type roleType =
  | "bcba"
  | "admin"
  | "frontera-internal"
  | "rbt"
  | "assessment-bcba"
  | "super_admin"
  | "psychologist"
  | "evaluation-psychologist"
  | "caregiver"
  | "enterprise-admin"
  | "frontera-clarity-provider";

type RoleMapping = {
  [key in AllRoutes]: roleType[];
};

// Roles routed into the provider onboarding flow (the gate). Every provider role
// gets at least the profile step; only BCBAs additionally get EHR-connect/import
// (enforced in OnboardingPage). Excludes caregiver (not a Provider — carries no
// ProviderMetadata.onboardingCompleted flag), frontera-internal / super_admin
// (internal), and frontera-clarity-provider (separate product, own onboarding).
// Shared by the first-login redirect gate (layouts/index.tsx) and the <Authorize>
// wrapper on the /onboarding route.
export const ONBOARDING_ROLES: roleType[] = [
  "bcba",
  "assessment-bcba",
  "admin",
  "rbt",
  "psychologist",
  "evaluation-psychologist",
];

// Flags that influence the post-login landing destination.
interface RootRedirectFlags {
  "portal-home-page"?: boolean;
  "db-single-page"?: boolean;
  "ia-clinical-merge"?: boolean;
}

// The report-writing clinical roles that can reach the Portal Home Page
// (PROA-1081). BCBAs land on /home in any flag state — their reassessment / IA
// report detail routes work without db-single-page. Psychologists are the full
// audience only under db-single-page (see homeRolesForFlags): their diagnosis-
// evaluation report *details* live exclusively under the unified
// /clinical-reports/evaluations tree, which DbSinglePageRedirect turns into
// NotAllowedPage when the flag is off — so with it off they belong in the legacy
// /evaluations flow end to end, not /home. This full set is the coarse role gate
// for the /home route's <Authorize> and roleMapping; the db-single-page
// refinement for psychologists is applied by homeRolesForFlags (redirect + nav
// item) and HomePageRouteGate (route).
const HOME_ROLES: roleType[] = ["assessment-bcba", "bcba", "psychologist", "evaluation-psychologist"];

const PSYCH_HOME_ROLES: roleType[] = ["psychologist", "evaluation-psychologist"];

// Effective /home audience given feature flags. Psychologists are included only
// when db-single-page is on (their report details require it). Shared by the
// post-login redirect, the "Home" nav item, and HomePageRouteGate so all three
// agree on exactly who gets /home in the current flag state.
export const homeRolesForFlags = (flags: Pick<RootRedirectFlags, "db-single-page">): roleType[] =>
  flags["db-single-page"] ? HOME_ROLES : HOME_ROLES.filter((r) => !PSYCH_HOME_ROLES.includes(r));

// Decides where a user landing on "/" is redirected, by role. Returns the target
// path, or null when no role matches (the caller then renders the normal layout
// instead of redirecting). Order matters and mirrors the historical precedence:
// Frontera Clarity org → Portal Home Page (HOME_ROLES, behind portal-home-page) →
// psychologist → admin → assessment-bcba → bcba/rbt. With
// portal-home-page off, this resolves exactly as it did before the homepage.
// D2P/caregiver users are excluded entirely (PAR-1709) — they have no portal
// landing and no route access; the mobile app is their only client.
export const resolveRootRedirect = (args: {
  roles: string[];
  isFronteraClarityOrg: boolean;
  flags: RootRedirectFlags;
}): AllRoutes | null => {
  const { roles, isFronteraClarityOrg, flags } = args;
  if (isFronteraClarityOrg || roles.includes("frontera-clarity-provider")) return "/frontera-clarity";
  // Portal Home Page (PROA-1081): BCBAs always land here when the flag is on;
  // psychologists only when db-single-page is also on (homeRolesForFlags encodes
  // that rule). Ordered ABOVE the admin and psychologist branches, so a user who
  // holds an eligible role *and* admin still gets /home rather than their prior
  // landing — by product decision, the home experience wins. Everyone else (incl.
  // psychologists when db-single-page is off) falls through: the psychologist
  // branch below is their flag-OFF fallback.
  const homeRoles = homeRolesForFlags(flags) as string[];
  if (flags["portal-home-page"] && roles.some((r) => homeRoles.includes(r))) return "/home";
  if (roles.includes("psychologist") || roles.includes("evaluation-psychologist"))
    return flags["db-single-page"] ? "/clinical-reports" : "/evaluations";
  if (roles.includes("admin")) return "/clients";
  if (roles.includes("assessment-bcba"))
    return flags["ia-clinical-merge"] ? "/clinical-reports" : "/assessment-reports";
  if (roles.includes("bcba") || roles.includes("rbt")) return "/video-sessions";
  return null;
};

export const roleMapping: RoleMapping = {
  // Portal Home Page (PROA-1081) — the report-writing clinical roles, shared with
  // resolveRootRedirect so redirect, nav item, and route <Authorize> agree.
  "/home": HOME_ROLES,
  "/clients": ["admin", "assessment-bcba", "bcba", "psychologist", "evaluation-psychologist"],
  "/assessment-reports": ["assessment-bcba", "bcba"],
  "/assessment-reports-legacy": ["assessment-bcba", "bcba"],
  "/assessment-reports?trigger-walkthrough=true": ["assessment-bcba", "bcba"],
  "/downloads": ["assessment-bcba", "psychologist", "bcba", "evaluation-psychologist"],
  "/session-analyzer": ["admin"],
  "/evaluations": ["psychologist", "evaluation-psychologist"],
  "/video-sessions": ["rbt", "bcba", "admin"],
  "/video-sessions-v2": ["frontera-internal"],
  "/analytics": ["admin", "bcba"],
  "/admin-analytics": ["admin", "frontera-internal"],
  "/clinical-reports": ["assessment-bcba", "psychologist", "bcba", "evaluation-psychologist"],
  "/templates": ["admin", "frontera-internal"],
  "/frontera-clarity": ["frontera-clarity-provider", "frontera-internal"],
  "/frontera-clarity/expert-chat": ["frontera-clarity-provider", "frontera-internal"],
  "/frontera-clarity/billing": ["frontera-clarity-provider", "frontera-internal"],
  "/frontera-clarity/scheduling": ["frontera-clarity-provider", "frontera-internal"],
  "/frontera-clarity/my-availability": ["frontera-clarity-provider", "frontera-internal"],
  "/notifications/organization": ["admin", "frontera-internal"],
  "/notifications/user": [
    "bcba",
    "admin",
    "frontera-internal",
    "rbt",
    "assessment-bcba",
    "psychologist",
    "evaluation-psychologist",
    "enterprise-admin",
    "frontera-clarity-provider",
  ],
  "/integrations": ["admin", "bcba"],
  "/settings/profile": ["frontera-clarity-provider"],
  "/settings/signature": ["assessment-bcba", "psychologist", "bcba", "evaluation-psychologist"],
  "/qa-criteria": ["frontera-internal"],
  "/leads": ["admin", "frontera-internal"],
  "/clinics": ["admin", "frontera-internal"],
  "/goal-bank": ["admin", "frontera-internal"],
  "/onboarding": ONBOARDING_ROLES,
  "/release-notes": HOME_ROLES,
  "/members": ["admin"],
};
